import React, { useContext } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { routeRolloutRegistry } from '@usealloy/daffy-to-frontend-dashboard-rollout';
import {
  Spinner, Text, Toggle, Icon, IconName,
} from '@usealloy/component-library';
import { UserContext } from '../../context';
import { BetaPreferencesProvider, useBetaToggle } from '../../BetaPreferencesProvider';
import { findMatchedGroupAndPath } from '../../frontend-dashboard-rollout/helpers';
import {
  BANNER_COPY,
  BANNER_HEIGHT,
  FEEDBACK_LABEL,
  FEEDBACK_PENDO_ANCHOR,
  LEARN_MORE_URL,
} from './constants';

/**
 * The bar itself. Not built on the component-library `Banner`: that one is a rounded, bordered,
 * in-page notification box, and this is full-bleed shell chrome at a fixed height.
 */
const StyledBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: ${BANNER_HEIGHT};
  padding-inline: 16px;
  width: 100%;

  /* Same token the component-library Banner uses for its 'info' variant. */
  background-color: var(--cl-juicyBlue-100);
`;

/** The copy, which wins the leftover space so the controls stay pinned to the trailing edge. */
const StyledMessage = styled(Text)`
  min-width: 0;

  > a {
    text-transform: unset;
    font-size: var(--cl-font-size-2);
    line-height: var(--cl-font-size-2);
    font-weight: var(--cl-font-weight-regular);
  }
`;

/**
 * The feedback affordance, styled as a text link.
 *
 * A real `button` rather than `Text`: the element has to be focusable and keyboard-operable, and
 * `Text` cannot render one (`TextElement` has no `button`). Pendo binds the click out-of-repo via
 * `data-pendo-anchor` and opens a modal; Enter and Space on a native button both dispatch a click,
 * so keyboard users reach that modal without any handler of our own.
 *
 * Not the global `.false-link` class: it forces uppercase and a 5px horizontal margin, and keys off
 * `:focus` rather than `:focus-visible`, so it would underline after mouse clicks too.
 */
const StyledFeedbackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--cl-space-1);

  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  color: var(--cl-text-link);
  font-family: inherit;
  font-size: var(--cl-font-size-2);
  line-height: var(--cl-line-height-2);
  /* base.scss makes every <a>/<button> bold; the banner copy around this is regular. */
  font-weight: var(--cl-font-weight-regular);
  text-transform: none;
  text-decoration: none;

  /*
   * The underline is the focus indicator as well as the hover affordance — no focus ring here, so
   * this reads as a text link rather than a focusable control.
   */
  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
  }
`;

/** Feedback affordance + toggle, on the trailing edge. */
const StyledControls = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cl-space-4);

  /*
   * The Toggle wraps itself in a Box that takes the width of the row. Neither the styles nor the
   * className prop reaches that Box, so a styled(Toggle) cannot shrink it — hence this child
   * selector. The Spinner is excluded by its label: it renders into the same slot mid-write.
   */
  > div:not([aria-label='Loading']) {
    width: unset;
  }
`;

/**
 * Persistent beta banner for the beta window, mounted at the top of the legacy dashboard shell.
 *
 * A second consumer of the shared beta-preferences controller (`source='banner'`) — not a new
 * preference mechanism. The write lifecycle, coupling, telemetry, nav strategy, and failure
 * snackbar all stay in `BetaPreferencesProvider`; this surface owns only its copy and layout.
 *
 * The LaunchDarkly kill switch is checked *before* the provider mounts, so an org with the banner
 * off pays no context and no refetch-on-focus listener (FRONTEND-653).
 */
export default function BetaBanner() {
  const { featureFlags } = useContext(UserContext);

  if (!featureFlags?.betaBannerEnabled) {
    return null;
  }

  return (
    <BetaPreferencesProvider source="banner">
      <BetaBannerBar />
    </BetaPreferencesProvider>
  );
}

/**
 * The beta banner bar.
 *
 * Toggles the beta area of the page the agent is currently on: the key comes from the route group
 * matching the pathname, never a hardcoded cookie. Until FRONTEND-694 splits the per-area cookies
 * every group resolves to `betaRoutes`, so this behaves exactly as a global toggle today and
 * becomes per-area with no change here.
 *
 * Renders nothing when the page belongs to no route group — a toggle that cannot name what it
 * switches is worse than no banner (Open Question #8).
 */
function BetaBannerBar() {
  const { pathname } = useLocation();

  const matched = findMatchedGroupAndPath({
    routeRegistry: routeRolloutRegistry,
    daffyPathname: pathname,
  });

  // Called unconditionally — `useBetaToggle` tolerates an undefined key and reports
  // `available: false`, so the hook order stays stable as the agent navigates.
  const {
    available, checked, disabled, loading, setEnabled,
  } = useBetaToggle(matched?.matchedRouteGroup.cookie);

  if (!matched || !available) {
    return null;
  }

  const copy = BANNER_COPY[matched.matchedRouteGroup.featureFlag];
  if (!copy) {
    return null;
  }

  const { body, lead, linkLabel } = checked ? copy.on : copy.off;

  return (
    <StyledBar role="region" aria-label="Beta program">
      <StyledMessage>
        <Icon icon={IconName.Bell} size="small" />
        <Text as="span" weight={700}>{lead}</Text>
        {Boolean(body) && <Text as="span">{body}</Text>}
        {LEARN_MORE_URL && (
          <a href={LEARN_MORE_URL} rel="noreferrer" target="_blank">
              {linkLabel}
            <Icon icon={IconName.ArrowUpRightFromSquare} size="small" style={{ marginLeft: 'var(--cl-space-1)', color: 'inherit' }} />
          </a>
        )}
      </StyledMessage>

      <StyledControls>
        <StyledFeedbackButton
          aria-haspopup="dialog"
          data-pendo-anchor={FEEDBACK_PENDO_ANCHOR}
          type="button"
        >
          {FEEDBACK_LABEL}
          <Icon icon={IconName.Message} size="small" style={{ color: 'inherit' }} />
        </StyledFeedbackButton>
        {loading ? <Spinner aria-label="Loading" size="small" />
          : (
            <Toggle
              aria-label="Beta"
              checked={checked}
              disabled={disabled}
              onCheckedChange={next => setEnabled(next)}
            />
          )}
      </StyledControls>
    </StyledBar>
  );
}
